import React from "react";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  HStack,
  Button,
  Box,
  Divider,
  Icon,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { CheckCircleIcon, WarningIcon, InfoIcon } from "@chakra-ui/icons";

interface SharingResult {
  validAccessorEmails: string[];
  invalidAccessorEmails: string[];
  alreadySharedAccessorEmails: string[];
  newShareCount: number;
}

interface SharingResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: SharingResult;
}

const SharingResultsModal: React.FC<SharingResultsModalProps> = ({
  isOpen,
  onClose,
  result,
}) => {
  const intl = useIntl();

  const EmailSection: React.FC<{
    title: string;
    description: string;
    emails: string[];
    icon: any;
    iconColor: string;
  }> = ({ title, description, emails, icon, iconColor }) => {
    if (emails.length === 0) return null;

    return (
      <Box mb={6}>
        <HStack mb={3} align="center">
          <Icon as={icon} color={iconColor} boxSize={5} />
          <Text
            fontSize="16px"
            fontWeight="bold"
            color="brand.900"
            fontFamily="Calibri (Body)"
          >
            {title}
          </Text>
        </HStack>
        <Text
          fontSize="14px"
          color="gray.600"
          mb={3}
          fontFamily="Calibri (Body)"
        >
          {description}
        </Text>
        <List spacing={2} pl={4}>
          {emails.map((email, index) => (
            <ListItem key={index}>
              <ListIcon as={CheckCircleIcon} color={iconColor} />
              <Text as="span" fontSize="14px" fontFamily="Calibri (Body)">
                {email}
              </Text>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Modal isCentered size="md" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        padding="10px"
        maxWidth="600px"
        width="100%"
        borderRadius="30px"
        maxHeight="80vh"
      >
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <Text
            color="brand.900"
            fontSize="18px"
            fontWeight="bold"
            fontFamily="Calibri (Body)"
            mb={6}
            textAlign="center"
          >
            {intl.formatMessage({ id: "sharingResults" })}
          </Text>

          <VStack spacing={4} align="stretch">
            {/* Successfully shared emails */}
            <EmailSection
              title={intl.formatMessage({ id: "newlySharedEmails" })}
              description={intl.formatMessage({
                id: "newlySharedEmailsDescription",
              })}
              emails={result.validAccessorEmails}
              icon={CheckCircleIcon}
              iconColor="green.500"
            />

            {/* Already shared emails */}
            <EmailSection
              title={intl.formatMessage({ id: "alreadySharedEmails" })}
              description={intl.formatMessage({
                id: "alreadySharedEmailsDescription",
              })}
              emails={result.alreadySharedAccessorEmails}
              icon={InfoIcon}
              iconColor="blue.500"
            />

            {/* Invalid emails */}
            <EmailSection
              title={intl.formatMessage({ id: "invalidEmails" })}
              description={intl.formatMessage({
                id: "invalidEmailsDescription",
              })}
              emails={result.invalidAccessorEmails}
              icon={WarningIcon}
              iconColor="orange.500"
            />

            {/* Show message if no emails in any category */}
            {result.validAccessorEmails.length === 0 &&
              result.alreadySharedAccessorEmails.length === 0 &&
              result.invalidAccessorEmails.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Text
                    fontSize="14px"
                    color="gray.500"
                    fontFamily="Calibri (Body)"
                  >
                    {intl.formatMessage({ id: "noEmailsInCategory" })}
                  </Text>
                </Box>
              )}
          </VStack>

          <Divider my={6} />

          <HStack justify="center">
            <Button
              variant="outline"
              colorScheme="gray"
              onClick={onClose}
              borderRadius="20px"
              px={8}
            >
              {intl.formatMessage({ id: "close" })}
            </Button>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SharingResultsModal; 